// Google Analytics utility functions

// Global window interface extension
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Type definitions for better TypeScript support
interface GTMEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  custom_parameter?: string;
}

// Check if Google Analytics is properly initialized
export const isGAInitialized = (): boolean => {
  const GA_MEASUREMENT_ID = (process.env as any).REACT_APP_GA_MEASUREMENT_ID;
  return !!(
    typeof window !== 'undefined' && 
    typeof window.gtag === 'function' && 
    GA_MEASUREMENT_ID && 
    GA_MEASUREMENT_ID !== 'GA_MEASUREMENT_ID'
  );
};

// Initialize Google Analytics
export const initializeGA = (): void => {
  const GA_MEASUREMENT_ID = (process.env as any).REACT_APP_GA_MEASUREMENT_ID;
  
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'GA_MEASUREMENT_ID') {
    console.log('Google Analytics: No valid measurement ID found');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function if it doesn't exist
  if (typeof window.gtag !== 'function') {
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }

  // Check if script is already loaded
  const existingScript = document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`);
  if (!existingScript) {
    // Set initial timestamp
    window.gtag('js', new Date());

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Configure GA with privacy settings
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true, // GDPR compliance
      cookie_expires: 28 * 24 * 60 * 60, // 28 days
      send_page_view: false, // We'll send page views manually
    });

    console.log('Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  }
};

// Track page views
export const trackPageView = (pagePath?: string, pageTitle?: string): void => {
  if (!isGAInitialized()) return;
  
  const GA_MEASUREMENT_ID = (process.env as any).REACT_APP_GA_MEASUREMENT_ID;
  
  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
      page_location: window.location.href,
    });
  }
};

// Track custom events
export const trackEvent = (action: string, parameters: GTMEventParams = {}): void => {
  if (!isGAInitialized()) return;
  
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, parameters);
  }
};

// Specific tracking functions for common events
export const trackDownload = (platform: 'ios' | 'android'): void => {
  trackEvent('click', {
    event_category: 'download',
    event_label: platform === 'ios' ? 'app_store' : 'play_store',
    value: 1,
  });
};

export const trackFeedback = (type: 'email' | 'portal'): void => {
  trackEvent('click', {
    event_category: 'feedback',
    event_label: type === 'email' ? 'email_support' : 'userjot_portal',
  });
};

export const trackEngagement = (timeSpent: number): void => {
  trackEvent('engagement', {
    event_category: 'engagement',
    event_label: 'time_on_page',
    value: timeSpent,
  });
};

export const trackContact = (type: 'email' | 'privacy' | 'terms'): void => {
  trackEvent('click', {
    event_category: type === 'email' ? 'contact' : 'legal',
    event_label: type,
  });
};

// Debug function to check GA status
export const debugGA = (): void => {
  const GA_MEASUREMENT_ID = (process.env as any).REACT_APP_GA_MEASUREMENT_ID;
  console.log('Google Analytics Debug Info:');
  console.log('- Measurement ID:', GA_MEASUREMENT_ID);
  console.log('- gtag function exists:', typeof window.gtag === 'function');
  console.log('- dataLayer exists:', !!window.dataLayer);
  console.log('- Is initialized:', isGAInitialized());
};








